<template>
  <transition name="modal-fade">
    <div v-if="visible" class="cart-modal-overlay" @click.self="handleClose">
      <div class="cart-modal-container">
        <!-- 顶部标题栏 -->
        <div class="cart-modal-header">
          <h2 class="cart-modal-title">{{ $t('cart_modal.title') }}</h2>
          <div class="header-actions">
            <button class="header-btn btn-dark" @click="handleCoupon">{{ $t('cart_modal.coupon') }}</button>
            <button class="header-btn btn-grey" @click="handlePrevious" v-if="currentStep !== 4">{{ $t('cart_modal.previous') }}</button>
            <button class="header-btn btn-dark" @click="handleClose" v-if="currentStep === 4">{{ $t('common.close') }}</button>
            <button class="header-btn btn-dark continue-btn" @click="handleContinue">{{ currentStep === 4 ? $t('cart_modal.finish') : $t('cart_modal.continue') }}</button>
          </div>
        </div>

        <!-- 进度步骤 -->
        <div class="checkout-steps">
          <span class="step" :class="stepClass(1)">{{ $t('cart_modal.steps.step1') }}</span>
          <span class="step-chevron">›</span>
          <span class="step" :class="stepClass(2)">{{ $t('cart_modal.steps.step2') }}</span>
          <span class="step-chevron">›</span>
          <span class="step" :class="stepClass(3)">{{ $t('cart_modal.steps.step3') }}</span>
          <span class="step-chevron">›</span>
          <span class="step" :class="stepClass(4)">{{ $t('cart_modal.steps.step4') }}</span>
        </div>

        <!-- 汇总栏 -->
        <div class="summary-bar">
          <div class="summary-segment segment-dark" @click="goToStep(1)">
            <span class="summary-label">{{ $t('cart_modal.summary.products') }}</span>
            <span class="summary-value">{{ cartItems.length }}</span>
          </div>
          <div class="summary-segment segment-gold">
            <span class="summary-label">{{ $t('cart_modal.summary.subtotal') }}</span>
            <span class="summary-value">${{ formattedSubtotal }}</span>
          </div>
          <div v-if="memberDiscountNum > 0" class="summary-segment segment-grey">
            <span class="summary-label">{{ $t('cart_modal.summary.member_discount') }}</span>
            <span class="summary-value">-${{ formatPrice(memberDiscountNum) }}</span>
          </div>
          <!-- 调试信息（开发时可查看） -->
          <!-- <div style="display:none">
            nowrank: {{ user?.nowrank?.discount || 'N/A' }}
            discount: {{ memberDiscountNum }}
          </div> -->
          <div class="summary-segment segment-grey">
            <span class="summary-label">{{ $t('cart_modal.summary.coupon_discount') }}</span>
            <span class="summary-value">${{ formatPrice(couponDiscountNum) }}</span>
          </div>
          <div class="summary-segment segment-grey">
            <span class="summary-label">{{ $t('cart_modal.summary.shipping') }}</span>
            <span class="summary-value">${{ formattedShipping }}</span>
          </div>
          <div class="summary-segment segment-dark segment-total">
            <span class="summary-label">{{ $t('cart_modal.summary.order_total') }}</span>
            <span class="summary-value total-value">${{ formattedOrderTotal }}</span>
          </div>
        </div>

        <!-- 積分欄（登入後、第一步及第三步顯示） -->
        <div v-if="(currentStep === 1 || currentStep === 3) && isLoggedIn && cartItems.length > 0" class="points-bar">
          <div class="points-logo">
            <img :src="timeClubLogo" alt="TIME Club" />
          </div>
          <div class="points-item">
            <span class="points-label">{{ $t('cart_modal.points.balance') }}</span>
            <span class="points-value">{{ formatPoints(pointsBalance) }}</span>
          </div>
          <div class="points-item">
            <span class="points-label">{{ $t('cart_modal.points.used') }}</span>
            <span class="points-value">{{ formatPoints(pointsUsed) }}</span>
          </div>
          <div class="points-item">
            <span class="points-label">{{ $t('cart_modal.points.earnable') }}</span>
            <span class="points-value">{{ formatPoints(earnablePoints) }}</span>
          </div>
          <div class="points-item">
            <span class="points-label">{{ $t('cart_modal.points.total') }}</span>
            <span class="points-value points-total">{{ formatPoints(latestPointsTotal) }}</span>
          </div>
        </div>

        <!-- 第一步：商品列表 -->
        <div v-if="currentStep === 1" class="cart-items-body">
          <div v-if="cartItems.length === 0" class="cart-empty">
            <p>{{ $t('cart_modal.empty') }}</p>
            <button class="continue-shopping-btn" @click="handleClose">{{ $t('cart_modal.continue_shopping') }}</button>
          </div>

          <div
            v-for="item in cartItems"
            :key="item.id"
            class="cart-item-row"
          >
            <div class="item-thumb">
              <img v-if="item.image" :src="resolveImage(item.image)" :alt="item.nameCn" />
              <div v-else class="thumb-placeholder">{{ item.brandCn || item.brand }}</div>
            </div>

            <div class="item-details">
              <div class="item-tags">
                <span class="item-brand-tag">{{ item.brandCn || item.brand }}</span>
                <!-- <span v-if="isDiscounted(item)" class="item-promo-tag">{{ item.promoLabel || $t('cart_modal.promo_label') }}</span> -->
              </div>
              <h3 class="item-name">{{ item.nameCn || item.name }}</h3>
              <p class="item-spec">{{ formatSpec(item.spec) }}</p>
              <p class="item-sku">SKU: {{ item.sku || defaultSku(item.id) }}</p>
            </div>

            <div class="item-unit-price">
              <span class="current-price">USD ${{ item.price }}</span>
              <span v-if="item.originalPrice" class="original-price">${{ item.originalPrice }}</span>
            </div>

            <div class="item-quantity">
              <button class="qty-btn" @click="decreaseQty(item)">−</button>
              <input
                type="number"
                :value="item.quantity"
                min="1"
                max="99"
                @change="onQtyChange(item, $event)"
              />
              <button class="qty-btn" @click="increaseQty(item)">+</button>
            </div>

            <div class="item-line-total">
              <!-- <span v-if="isDiscounted(item)" class="discount-badge">{{ $t('cart_modal.discounted_item') }}</span> -->
              <span class="line-total-value">$ {{ formatPrice(lineTotal(item)) }}</span>
            </div>

            <button class="item-remove" @click="removeItem(item)" :aria-label="$t('common.remove')">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- 第二步：帳單及送貨設定 -->
        <div v-else-if="currentStep === 2" class="checkout-form-body">
          <div class="checkout-layout">
            <!-- 左侧：订单提交信息 -->
            <section class="checkout-left">
              <h3 class="checkout-page-title">
                <svg class="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                {{ $t('cart_modal.checkout_title') }}
              </h3>

              <!-- 发货方式 -->
              <div class="checkout-block">
                <h4 class="checkout-block-title">
                  <svg class="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                  {{ $t('cart_modal.delivery_settings') }}
                </h4>
                <div class="delivery-options">
                  <label
                    v-for="opt in pickupOptions"
                    :key="opt.value"
                    class="delivery-option"
                    :class="{ active: deliveryForm.pickupMethod === opt.value }"
                  >
                    <input
                      v-model="deliveryForm.pickupMethod"
                      type="radio"
                      :value="opt.value"
                      class="delivery-option-input"
                    />
                    <span class="delivery-option-info">
                      <span class="delivery-option-name">{{ opt.label }}</span>
                      <span class="delivery-option-desc">{{ opt.desc }}</span>
                    </span>
                    <span class="delivery-option-fee">{{ opt.fee }}</span>
                  </label>
                </div>
              </div>

              <!-- 收货信息 -->
              <div class="checkout-block">
                <h4 class="checkout-block-title">
                  <svg class="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  {{ $t('cart_modal.receiver_info') }}
                </h4>
                <p class="checkout-block-tip">{{ $t('cart_modal.receiver_tip') }}</p>
                <div class="receiver-form">
                  <div class="checkout-field">
                    <label class="checkout-label">{{ $t('cart_modal.contact_name') }}</label>
                    <input v-model="billingForm.name" autocomplete="off" type="text" class="checkout-input" :placeholder="$t('cart_modal.contact_name')" />
                  </div>
                  <div class="checkout-field">
                    <label class="checkout-label">{{ $t('cart_modal.contact_phone') }}</label>
                    <input v-model="billingForm.phone" type="tel" class="checkout-input" :placeholder="$t('cart_modal.contact_phone')" />
                  </div>
                  <div class="checkout-field">
                    <label class="checkout-label">{{ $t('cart_modal.contact_address') }}</label>
                    <input v-model="billingForm.address" type="text" class="checkout-input" :placeholder="$t('cart_modal.contact_address')" />
                  </div>
                </div>
              </div>
            </section>

            <!-- 右侧：商品详情与汇总 -->
            <section class="checkout-right">
              <div class="checkout-block checkout-summary-block">
                <h4 class="checkout-block-title">
                  <svg class="title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                  {{ $t('cart_modal.product_details') }}
                </h4>

                <div class="checkout-product-list">
                  <div v-for="item in cartItems" :key="item.id" class="checkout-product-item">
                    <div class="product-thumb">
                    <img v-if="item.image" :src="resolveImage(item.image)" :alt="item.nameCn" />
                    <div v-else class="thumb-placeholder">{{ item.brandCn || item.brand }}</div>
                  </div>
                    <div class="product-info">
                      <p class="product-name">{{ item.nameCn || item.name }}</p>
                      <p class="product-spec">{{ $t('cart_modal.spec_prefix') }}: {{ formatSpec(item.spec) }}；{{ $t('cart_modal.qty_prefix') }}: {{ item.quantity }}</p>
                    </div>
                    <div class="product-price">${{ formatPrice(lineTotal(item)) }}</div>
                  </div>
                </div>

                <div class="checkout-field remark-field">
                  <label class="checkout-label">{{ $t('cart_modal.remark_label') }}</label>
                  <textarea
                    v-model="orderRemark"
                    class="checkout-textarea"
                    rows="3"
                    :placeholder="$t('cart_modal.remark_placeholder')"
                  />
                </div>

                <div class="checkout-amounts">
                  <div class="amount-row">
                    <span>{{ $t('cart_modal.summary.subtotal') }} · {{ cartItems.length }}{{ $t('cart_modal.item_count_suffix') }}</span>
                    <span>${{ formattedSubtotal }}</span>
                  </div>
                  <div class="amount-row">
                    <span>{{ $t('cart_modal.summary.shipping') }}</span>
                    <span>${{ formattedShipping }}</span>
                  </div>
                  <div class="amount-row amount-total">
                    <span>{{ $t('cart_modal.summary.order_total') }}</span>
                    <span>USD ${{ formattedOrderTotal }}</span>
                  </div>
                </div>

                <button type="button" class="submit-order-btn" @click="handleContinue">
                  <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {{ $t('cart_modal.submit_order') }}
                </button>
                <p class="checkout-security-note">
                  <svg class="security-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  {{ $t('cart_modal.security_note') }}
                </p>
              </div>
            </section>
          </div>
        </div>

        <!-- 第三步：訂單確認及付款 -->
        <div v-else-if="currentStep === 3" class="checkout-payment-body">
          <div class="checkout-payment-left">
            <section class="payment-section">
              <h3 class="payment-section-title">{{ $t('cart_modal.payment_method') }}</h3>
              <div class="payment-options">
                <button
                  v-for="opt in paymentOptions"
                  :key="opt.value"
                  type="button"
                  class="payment-option"
                  :class="{ active: paymentMethod === opt.value }"
                  @click="paymentMethod = opt.value"
                >
                  {{ opt.label }}
                </button>
              </div>
              <div v-if="paymentMethod === 'u-shield'" class="checkout-payment-right display-none">
                <div class="usdt-payment-panel">
                  <!-- <p class="usdt-payment-title">{{ $t('cart_modal.u_shield_modal.title') }}</p> -->
                  <img v-if="usdtPayImg" :src="resolveImage(usdtPayImg)" alt="USDT Payment QR" class="usdt-payment-qr" />
                  <div class="usdt-payment-addr-section">
                    <p class="usdt-payment-addr-label">{{ $t('cart_modal.u_shield_modal.address_label') }}</p>
                    <p class="usdt-payment-addr-text" @click="copyUsdtAddr">{{ usdtPayAddr || $t('cart_modal.u_shield_modal.no_address') }}</p>
                    <p class="usdt-payment-addr-hint">{{ $t('cart_modal.u_shield_modal.copy_hint') }}</p>
                    <p class="usdt-payment-desc">{{ $t('cart_modal.u_shield_modal.desc') }}</p>
                    <p class="usdt-payment-desc">{{ $t('cart_modal.u_shield_modal.desc2') }}</p>
                  </div>
                </div>
              </div>
            </section>

            <div class="payment-agreement">
              <p class="payment-agreement-title">{{ $t('cart_modal.payment_agreement.title') }}</p>
              <ul class="payment-agreement-list">
                <li>{{ $t('cart_modal.payment_agreement.rule1') }}</li>
              </ul>
              <p class="payment-agreement-note">{{ $t('cart_modal.payment_agreement.note') }}</p>
            </div>

            <label class="age-confirm">
              <input v-model="ageConfirmed" type="checkbox" class="age-confirm-input" />
              <span class="age-confirm-text">{{ $t('cart_modal.age_confirmation') }}</span>
            </label>
          </div>

          <div v-if="paymentMethod === 'u-shield'" class="checkout-payment-right">
            <div class="usdt-payment-panel">
              <!-- <p class="usdt-payment-title">{{ $t('cart_modal.u_shield_modal.title') }}</p> -->
              <img v-if="usdtPayImg" :src="resolveImage(usdtPayImg)" alt="USDT Payment QR" class="usdt-payment-qr" />
              <div class="usdt-payment-addr-section">
                <p class="usdt-payment-addr-label">{{ $t('cart_modal.u_shield_modal.address_label') }}</p>
                <p class="usdt-payment-addr-text" @click="copyUsdtAddr">{{ usdtPayAddr || $t('cart_modal.u_shield_modal.no_address') }}</p>
                <p class="usdt-payment-addr-hint">{{ $t('cart_modal.u_shield_modal.copy_hint') }}</p>
                <p class="usdt-payment-desc">{{ $t('cart_modal.u_shield_modal.desc') }}</p>
                <p class="usdt-payment-desc">{{ $t('cart_modal.u_shield_modal.desc2') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 第三步底部確認按鈕 -->
        <div v-if="currentStep === 3" class="checkout-payment-footer">
          <button
            type="button"
            class="confirm-payment-btn"
            :class="{ 'is-loading': paying }"
            :disabled="paying"
            @click="handleConfirmPayment"
          >
            <span v-if="paying" class="payment-btn-spinner"></span>
            {{ paying ? $t('cart_modal.payment_processing') : $t('cart_modal.confirm_payment') }}
          </button>
        </div>

        <!-- 第四步：完成訂單 -->
        <div v-else-if="currentStep === 4" class="checkout-complete-body">
          <!-- <div class="payment-success-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <h3 class="payment-process-title">{{ $t('cart_modal.complete.payment_link') }}</h3> -->
          <div class="order-info-card">
            <div class="order-info-row">
              <span class="order-info-label">{{ $t('cart_modal.complete.order_number') }}</span>
              <span class="order-info-value">{{ confirmedOrderNumber }}</span>
            </div>
            <div class="order-info-row">
              <span class="order-info-label">{{ $t('cart_modal.complete.order_total') }}</span>
              <span class="order-info-value">$ {{ formatOrderAmount(confirmedOrderTotal) }}</span>
            </div>
          </div>
          <div class="payment-instruction-card">
            <p class="payment-instruction-main">{{ $t('cart_modal.complete.instruction') }}</p>
            <p class="payment-instruction-sub">{{ $t('cart_modal.complete.instruction_note') }}</p>
          </div>
          <div v-if="customerServiceQrCode" class="customer-service-card">
            <p class="customer-service-title">{{ $t('cart_modal.complete.customer_service_title') }}</p>
            <img :src="customerServiceQrCode" :alt="$t('cart_modal.complete.customer_service_title')" class="customer-service-qrcode" />
            <p class="customer-service-desc">{{ $t('cart_modal.complete.customer_service_desc') }}</p>
          </div>
        </div>
      </div>

      <!-- 優惠券彈窗 -->
      <transition name="modal-fade">
        <div v-if="showCouponModal" class="coupon-modal-overlay" @click.self="showCouponModal = false">
          <div class="coupon-modal-container">
            <div class="coupon-modal-header">
              <h3 class="coupon-modal-title">{{ $t('cart_modal.coupon_modal.title') }}</h3>
              <button class="coupon-modal-close" @click="showCouponModal = false">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="coupon-modal-body">
              <div v-if="couponLoading" class="coupon-empty">
                <p>{{ $t('cart_modal.coupon_modal.loading') }}</p>
              </div>
              <div v-else-if="availableCoupons.length === 0" class="coupon-empty">
                <p>{{ $t('cart_modal.coupon_modal.no_coupons') }}</p>
              </div>
              <div v-else class="coupon-list">
                <div
                  v-for="coupon in availableCoupons"
                  :key="coupon.id"
                  class="coupon-card"
                  :class="{ selected: selectedCoupon && selectedCoupon.id === coupon.id, 'coupon-disabled': !coupon.isApplicable }"
                  @click="selectCoupon(coupon)"
                >
                  <div class="coupon-card-left">
                    <div v-if="coupon.coupon_type === '4'" class="coupon-value-free-shipping">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                      </svg>
                      <span class="free-shipping-text">{{ $t('cart_modal.coupon_modal.free_shipping') }}</span>
                    </div>
                    <div v-else>
                      <div class="coupon-value-large">
                        <span class="coupon-currency-small">$</span>
                        <span class="coupon-amount-large">{{ coupon.value }}</span>
                      </div>
                      <div class="coupon-condition">{{ $t('cart_modal.coupon_modal.min_spend', { amount: coupon.minSpend }) }}</div>
                    </div>
                  </div>
                  <div class="coupon-card-right">
                    <div class="coupon-card-details">
                      <div class="coupon-card-header">
                        <h4 class="coupon-card-title">{{ coupon.title }}</h4>
                        <span v-if="coupon.coupon_type === '2'" class="coupon-type-badge type-product">{{ $t('cart_modal.coupon_modal.type_product') }}</span>
                        <span v-else-if="coupon.coupon_type === '3'" class="coupon-type-badge type-brand">{{ $t('cart_modal.coupon_modal.type_brand') }}</span>
                        <span v-else-if="coupon.coupon_type === '4'" class="coupon-type-badge type-shipping">{{ $t('cart_modal.coupon_modal.type_shipping') }}</span>
                      </div>
                      <p class="coupon-card-desc">{{ coupon.description }}</p>
                      <div class="coupon-card-validity">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {{ $t('cart_modal.coupon_modal.valid_until') }} {{ formatCouponDate(coupon.expiryDate) }}
                      </div>
                    </div>
                    <div class="coupon-card-check">
                      <svg v-if="selectedCoupon && selectedCoupon.id === coupon.id" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                  </div>
                  <div v-if="!coupon.isApplicable" class="coupon-not-applicable">
                    <span>{{ $t('cart_modal.coupon_modal.not_applicable_hint') }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="coupon-modal-footer">
              <button class="coupon-modal-btn btn-clear" @click="clearCoupon">{{ $t('cart_modal.coupon_modal.clear') }}</button>
              <button class="coupon-modal-btn btn-confirm" @click="confirmCoupon">{{ $t('cart_modal.coupon_modal.confirm') }}</button>
            </div>
          </div>
        </div>
      </transition>

      <!-- U盾轉賬提示彈窗 -->
      <transition name="modal-fade">
        <div v-if="showUSDTConfirmModal" class="usdt-confirm-overlay">
          <div class="usdt-confirm-container">
            <div class="usdt-confirm-spinner"></div>
            <p class="usdt-confirm-title">{{ $t('cart_modal.u_shield_modal.confirm_pending_title') }}</p>
            <p class="usdt-confirm-order">{{ $t('cart_modal.u_shield_modal.confirm_pending_order_label') }}：{{ confirmedOrderNumber }}</p>
          </div>
        </div>
      </transition>

      <!-- 支付寶/銀聯支付彈窗 -->
      <transition name="modal-fade">
        <div v-if="showAlipayModal" class="alipay-modal-overlay" @click.self="closeAlipayModal">
          <div :class="['alipay-modal-container', { 'chinapay-modal': isChinapayModal }]">
            <div class="alipay-modal-header">
              <h3 class="alipay-modal-title">{{ currentPaymentLabel }}</h3>
              <button class="alipay-modal-close" @click="closeAlipayModal">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="alipay-modal-body">
              <template v-if="isChinapayModal">
                <div class="chinapay-waiting">
                  <div class="chinapay-spinner"></div>
                  <p class="chinapay-waiting-text">{{ $t('cart_modal.chinapay_waiting') || '请在新窗口完成支付，支付完成后将自动跳转' }}</p>
                </div>
              </template>
              <template v-else>
                <iframe name="alipayFrame" class="alipay-iframe" frameborder="0"></iframe>
              </template>
            </div>
            <div class="alipay-modal-footer">
              <button
                type="button"
                class="alipay-paid-btn"
                :disabled="manualPayChecking"
                @click="handleManualPaidCheck"
              >
                {{ manualPayChecking ? $t('cart_modal.payment_checking') : $t('cart_modal.i_have_paid') }}
              </button>
              <p class="alipay-paid-hint">{{ $t('cart_modal.i_have_paid_hint') }}</p>
            </div>
          </div>
        </div>
      </transition>

      <!-- 地址簿彈窗 -->
      <transition name="modal-fade">
        <div v-if="showAddressModal" class="address-modal-overlay" @click.self="showAddressModal = false">
          <div class="address-modal-container">
            <div class="address-modal-header">
              <h3 class="address-modal-title">{{ $t('cart_modal.address_book.title') }}</h3>
              <button class="address-modal-close" @click="showAddressModal = false">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="address-modal-body">
              <div v-if="addressLoading" class="address-empty">
                <p>{{ $t('cart_modal.address_book.loading') }}</p>
              </div>
              <div v-else-if="savedAddresses.length === 0" class="address-empty">
                <p>{{ $t('cart_modal.address_book.no_address') }}</p>
              </div>
              <div
                v-for="addr in savedAddresses"
                :key="addr.id"
                class="address-item"
                @click="selectAddress(addr)"
              >
                <div class="address-item-info">
                  <p class="address-item-name">{{ addr.name || '' }} {{ getSalutationText(addr.salutation) }}</p>
                  <p class="address-item-phone">{{ addr.phone || '' }}</p>
                  <p class="address-item-detail">{{ getRegionText(addr.region) }} {{ addr.address || '' }}</p>
                </div>
                <button class="address-item-btn" @click.stop="selectAddress(addr)">{{ $t('cart_modal.address_book.select') }}</button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import { TIME_CLUB_LOGO } from '@/constants/benefits'
import { goodsApi } from '@/api/goods'
import { getFullUrl } from '@/api/request'

export default {
  name: 'CartModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      timeClubLogo: TIME_CLUB_LOGO,
      pointsUsed: 0,
      currentStep: 1,
      deliveryForm: {
        pickupMethod: 2
      },
      billingForm: {
        salutation: 'mr',
        name: '',
        phone: '',
        address: '',
        region: '',
        savedAddress: ''
      },
      shippingForm: {
        sameAsBilling: true,
        salutation: 'mr',
        name: '',
        phone: '',
        address: '',
        region: ''
      },
      orderRemark: '',
      showAddressModal: false,
      addressLoading: false,
      addressList: [],
      showCouponModal: false,
      couponLoading: false,
      couponList: [],
      selectedCoupon: null,
      showUShieldModal: false,
      showUSDTConfirmModal: false,
      showAlipayModal: false,
      isChinapayModal: false,
      paymentMethod: 'hk-wallet',
      ageConfirmed: false,
      confirmedOrderNumber: '',
      confirmedOrderTotal: 0,
      orderInfoLoading: false,
      paying: false,
      manualPayChecking: false,
      freightInfo: [],
      usdtPayImg: '',
      usdtPayAddr: '',
      customerServiceQrCode: '',
      payTimer: null,
      usdtQueryTimer: null,
      chinapayWindow: null,
      chinapayWindowName: ''
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters(['cartItems', 'isLoggedIn']),
    pickupOptions() {
      const feeText = (val) => {
        const fee = this.getFreightByType(val)
        return fee > 0 ? `$${this.formatPrice(fee)}` : this.$t('cart_modal.free')
      }
      // 送货方式由后端"送货方式管理"配置下发，动态渲染
      if (this.freightInfo.length) {
        return this.freightInfo.map(f => ({
          value: Number(f.type),
          label: f.name,
          desc: this.getFreightDesc(f.type) || (Number(f.is_pickup) === 1 ? this.$t('cart_modal.pickup_options.pickup_desc') : ''),
          fee: feeText(f.type)
        }))
      }
      // 接口未返回时的兜底选项
      const freeNote = this.$t('cart_modal.free_shipping_note')
      return [
        {
          value: 1,
          label: this.$t('cart_modal.pickup_options.express'),
          desc: freeNote,
          fee: feeText(1)
        },
        {
          value: 2,
          label: this.$t('cart_modal.pickup_options.pickup'),
          desc: this.$t('cart_modal.pickup_options.pickup_desc'),
          fee: feeText(2)
        }
      ]
    },
    paymentOptions() {
      return [
        { value: 'hk-wallet', label: this.$t('cart_modal.payment_options.hk_wallet') },
        { value: 'cn-wallet', label: this.$t('cart_modal.payment_options.cn_wallet') },
        { value: 'chinapay', label: this.$t('cart_modal.payment_options.chinapay') },
        { value: 'u-shield', label: this.$t('cart_modal.payment_options.u_shield') }
      ]
    },
    currentPaymentLabel() {
      const opt = this.paymentOptions.find(o => o.value === this.paymentMethod)
      return opt ? opt.label : ''
    },
    pointsBalance() {
      return this.user?.points || 0
    },
    earnablePoints() {
      // 使用折后金额计算可获得积分（小计 - 会员折扣 - 优惠券折扣）
      return Math.max(0, this.subtotalNum - this.discountNum)
    },
    latestPointsTotal() {
      return this.pointsBalance - this.pointsUsed + this.earnablePoints
    },
    subtotalNum() {
      return this.cartItems.reduce((sum, item) => sum + this.lineTotal(item), 0)
    },
    availableCoupons() {
      return this.couponList.filter(c => c.status === 'available').map(c => ({
        ...c,
        isApplicable: this.isCouponApplicable(c)
      }))
    },
    isFreeShippingCoupon() {
      return this.selectedCoupon && this.selectedCoupon.coupon_type === '4'
    },
    memberDiscountNum() {
      // 检查用户是否登录
      if (!this.user) return 0
      
      // 获取会员折扣信息
      const nowrank = this.user.nowrank || this.user.rank
      if (!nowrank) return 0
      
      // 解析折扣值（可能是字符串或数字）
      const discount = parseFloat(nowrank.discount || nowrank.zhekou || 10)
      
      // 折扣无效或折扣为10折（无折扣）时返回0
      if (isNaN(discount) || discount >= 10 || discount <= 0) return 0
      
      // 计算折扣金额 = 小计 * (10 - 折扣) / 10
      // 例如：8折(discount=8)，折扣金额 = 小计 * (10-8) / 10 = 小计 * 0.2
      return this.subtotalNum * (10 - discount) / 10
    },
    couponDiscountNum() {
      if (!this.selectedCoupon) return 0
      const minSpend = this.selectedCoupon.minSpend || 0
      if (minSpend > 0 && this.subtotalNum < minSpend) return 0
      // 免运费券不计算折扣金额
      if (this.selectedCoupon.coupon_type === '4') return 0
      return this.parsePrice(this.selectedCoupon.value)
    },
    discountNum() {
      return this.memberDiscountNum + this.couponDiscountNum
    },
    shippingNum() {
      if (this.cartItems.length === 0) return 0
      // 免运费券选择后免除运费
      if (this.isFreeShippingCoupon) return 0
      const freight = this.getFreightByType(this.deliveryForm.pickupMethod)
      return freight || 0
    },
    orderTotalNum() {
      return Math.max(0, this.subtotalNum - this.discountNum + this.shippingNum)
    },
    formattedSubtotal() {
      return this.formatPrice(this.subtotalNum)
    },
    formattedDiscount() {
      return this.formatPrice(this.discountNum)
    },
    formattedShipping() {
      return this.formatPrice(this.shippingNum)
    },
    formattedOrderTotal() {
      return this.formatPrice(this.orderTotalNum)
    },
    isStorePickup() {
      const item = this.freightInfo.find(f => String(f.type) === String(this.deliveryForm.pickupMethod))
      if (item) return Number(item.is_pickup) === 1
      return this.deliveryForm.pickupMethod === 2
    },
    selectedPickupLabel() {
      const opt = this.pickupOptions.find(o => o.value === this.deliveryForm.pickupMethod)
      return opt ? opt.label : ''
    },
    savedAddresses() {
      if (this.addressList.length) return this.addressList
      if (this.user && this.user.address) {
        return [{
          id: 'default',
          salutation: this.user.salutation || 'mr',
          name: this.user.name || '',
          phone: this.user.phone || '',
          region: this.user.region || '',
          address: this.user.address || ''
        }]
      }
      return []
    },
    billingDisplayName() {
      const name = this.billingForm.name || ''
      if (this.billingForm.salutation && name) {
        return `${name} ${this.billingForm.salutation}`
      }
      return name
    },
    billingDisplayPhone() {
      return this.billingForm.phone || ''
    }
  },
  watch: {
    async visible(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
        // 打开购物车时刷新用户信息，确保会员折扣数据最新
        if (this.isLoggedIn) {
          await this.$store.dispatch('fetchUserInfo')
        }
        this.loadCart()
        this.initBillingForm()
        this.fetchCustomerServiceQrCode()
      } else {
        this.currentStep = 1
        this.paymentMethod = 'hk-wallet'
        this.ageConfirmed = false
        this.confirmedOrderNumber = ''
        this.confirmedOrderTotal = 0
        this.orderRemark = ''
        this.selectedCoupon = null
        this.showUSDTConfirmModal = false
        this.stopUSDTQuery()
        if (!this.isOtherModalOpen()) {
          document.body.style.overflow = ''
        }
      }
    },
    'shippingForm.sameAsBilling'(val) {
      if (val) {
        this.shippingForm.salutation = this.billingForm.salutation
        this.shippingForm.name = this.billingForm.name
        this.shippingForm.phone = this.billingForm.phone
        this.shippingForm.address = this.billingForm.address
        this.shippingForm.region = this.billingForm.region
      }
    },
    'deliveryForm.pickupMethod'() {
      // 配送方式改变时，若已加载订单信息则重新获取
      if (this.currentStep === 2 && this.isLoggedIn && this.cartItems.length > 0) {
        this.fetchOrderInfo()
      }
    },
    subtotalNum(val) {
      if (this.selectedCoupon && this.selectedCoupon.minSpend > 0 && val < this.selectedCoupon.minSpend) {
        this.showToast({ message: this.$t('cart_modal.coupon_modal.min_spend_not_met'), type: 'warning' })
        this.selectedCoupon = null
      }
    }
  },
  methods: {
    ...mapActions(['updateCartItem', 'removeFromCart', 'showToast', 'openLoginModal', 'placeOrder', 'loadCart', 'fetchAddresses', 'fetchCoupons']),
    parsePrice(price) {
      return parseFloat(String(price).replace(/,/g, '')) || 0
    },
    formatPrice(num) {
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    resolveImage(src) {
      if (!src) return ''
      if (src.startsWith('http://') || src.startsWith('https://')) return src
      return getFullUrl(src)
    },
    formatPoints(num) {
      return this.formatPrice(num)
    },
    formatOrderAmount(num) {
      return num.toFixed(2)
    },
    generateOrderNumber() {
      const now = new Date()
      const pad = (n, len = 2) => String(n).padStart(len, '0')
      return `${pad(now.getFullYear() % 100)}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(Math.floor(Math.random() * 100))}`
    },
    isDiscounted(item) {
      return !!(item.originalPrice || item.promoLabel || item.isDiscounted)
    },
    lineTotal(item) {
      return this.parsePrice(item.price) * item.quantity
    },
    formatSpec(spec) {
      if (!spec) return ''
      return spec.replace(/(\d+支)\/(.+)/, '$1 / $2')
    },
    defaultSku(id) {
      return `TC-210000${String(id).padStart(4, '0')}`
    },
    async decreaseQty(item) {
      if (item.quantity <= 1) {
        // 数量为1时，不允许减少，提示用户删除
        this.showToast({ 
          message: this.$t('cart_modal.min_quantity_tip') || '数量最少为1，如需删除请点击删除按钮', 
          type: 'warning' 
        })
        return
      }
      await this.updateCartItem({ id: item.id, cartId: item.cartId, quantity: item.quantity - 1 })
    },
    async increaseQty(item) {
      await this.updateCartItem({ id: item.id, cartId: item.cartId, quantity: item.quantity + 1 })
    },
    async onQtyChange(item, event) {
      const val = parseInt(event.target.value, 10)
      
      // 检查输入值是否有效
      if (isNaN(val) || val < 1) {
        // 输入无效时恢复原值并提示
        event.target.value = item.quantity
        this.showToast({ 
          message: this.$t('cart_modal.invalid_quantity') || '请输入有效的数量', 
          type: 'warning' 
        })
        return
      }
      
      // 限制最大数量为99
      // if (val > 99) {
      //   event.target.value = 99
      //   this.showToast({ 
      //     message: this.$t('cart_modal.max_quantity_tip') || '单个商品最多购买99件', 
      //     type: 'warning' 
      //   })
      //   await this.updateCartItem({ id: item.id, cartId: item.cartId, quantity: 99 })
      //   return
      // }
      
      // 正常更新数量
      await this.updateCartItem({ id: item.id, cartId: item.cartId, quantity: val })
    },
    removeItem(item) {
      this.removeFromCart(item.cartId || item.id)
    },
    handleClose() {
      this.currentStep = 1
      this.$emit('close')
    },
    handlePrevious() {
      if (this.currentStep > 1) {
        this.currentStep -= 1
        return
      }
      this.handleClose()
    },
    goToStep(step) {
      if (step >= this.currentStep) return
      this.currentStep = step
    },
    stepClass(step) {
      if (this.currentStep === step) return 'active'
      if (this.currentStep > step) return 'completed'
      return ''
    },
    initBillingForm() {
      const user = this.user
      if (!user) return

      let name = user.name || ''
      if (!name && (user.lastName || user.firstName)) {
        name = `${user.lastName || ''} ${user.firstName || ''}`.trim()
      }

      this.billingForm = {
        salutation: user.salutation || 'mr',
        name,
        phone: user.phone || '',
        address: user.address || '',
        region: user.region || '',
        savedAddress: ''
      }

      this.shippingForm = {
        sameAsBilling: true,
        salutation: this.billingForm.salutation,
        name: this.billingForm.name,
        phone: this.billingForm.phone,
        address: this.billingForm.address,
        region: this.billingForm.region
      }
    },
    async openAddressModal() {
      this.showAddressModal = true
      if (!this.isLoggedIn) return
      this.addressLoading = true
      try {
        const list = await this.fetchAddresses()
        this.addressList = Array.isArray(list) ? list : []
      } catch (e) {
        console.error('获取地址列表失败', e)
        this.showToast({ message: this.$t('cart_modal.address_book.load_failed'), type: 'error' })
      } finally {
        this.addressLoading = false
      }
    },
    selectAddress(addr) {
      if (addr.salutation) this.shippingForm.salutation = addr.salutation
      if (addr.name !== undefined) this.shippingForm.name = addr.name
      if (addr.phone) this.shippingForm.phone = addr.phone
      if (addr.region) this.shippingForm.region = addr.region
      if (addr.address !== undefined) this.shippingForm.address = addr.address
      this.showAddressModal = false
    },
    validateBillingForm() {
      if (!this.billingForm.name.trim()) {
        this.showToast({ message: this.$t('cart_modal.validation.name_required'), type: 'warning' })
        return false
      }
      if (!this.billingForm.phone.trim()) {
        this.showToast({ message: this.$t('cart_modal.validation.phone_required'), type: 'warning' })
        return false
      }
      if (!this.isStorePickup && !this.billingForm.address.trim()) {
        this.showToast({ message: this.$t('cart_modal.validation.address_required'), type: 'warning' })
        return false
      }
      return true
    },
    requireLogin(action) {
      if (!this.isLoggedIn) {
        this.openLoginModal()
        return false
      }
      return true
    },
    getSalutationText(salutation) {
      const map = {
        mr: this.$t('cart_modal.salutation.mr'),
        mrs: this.$t('cart_modal.salutation.mrs'),
        ms: this.$t('cart_modal.salutation.ms')
      }
      return map[salutation] || salutation || ''
    },
    getRegionText(region) {
      const map = {
        'hong-kong-island': this.$t('cart_modal.regions.hong-kong-island'),
        'hong_kong': this.$t('cart_modal.regions.hong-kong-island'),
        kowloon: this.$t('cart_modal.regions.kowloon'),
        'new-territories': this.$t('cart_modal.regions.new-territories')
      }
      return map[region] || region || ''
    },
    async handleCoupon() {
      if (!this.requireLogin('coupon')) return
      this.showCouponModal = true
      this.couponLoading = true
      try {
        const list = await this.fetchCoupons()
        this.couponList = Array.isArray(list) ? list : []
      } catch (e) {
        console.error('获取优惠券列表失败', e)
        this.showToast({ message: this.$t('cart_modal.coupon_modal.load_failed'), type: 'error' })
      } finally {
        this.couponLoading = false
      }
    },
    isCouponApplicable(coupon) {
      // 全场通用和免运费券始终可用
      if (coupon.coupon_type === '1' || coupon.coupon_type === '4') return true
      const cartBrandIds = this.cartItems.map(item => String(item.brandId)).filter(Boolean)
      const cartGoodsIds = this.cartItems.map(item => String(item.goodsId)).filter(Boolean)
      // 特定商品券：购物车中有任意商品的ID在goods_ids中
      if (coupon.coupon_type === '2') {
        const couponGoodsIds = String(coupon.goods_ids).split(',').map(id => id.trim()).filter(Boolean)
        return couponGoodsIds.some(id => cartGoodsIds.includes(id))
      }
      // 特定品牌券：购物车中有任意商品的品牌ID在brand_ids中
      if (coupon.coupon_type === '3') {
        const couponBrandIds = String(coupon.brand_ids).split(',').map(id => id.trim()).filter(Boolean)
        return couponBrandIds.some(id => cartBrandIds.includes(id))
      }
      return true
    },
    selectCoupon(coupon) {
      if (!coupon.isApplicable) {
        this.showToast({ message: this.$t('cart_modal.coupon_modal.not_applicable'), type: 'warning' })
        return
      }
      const minSpend = coupon.minSpend || 0
      if (minSpend > 0 && this.subtotalNum < minSpend) {
        this.showToast({ message: this.$t('cart_modal.coupon_modal.min_spend_not_met'), type: 'warning' })
        return
      }
      this.selectedCoupon = coupon
    },
    clearCoupon() {
      this.selectedCoupon = null
      this.showCouponModal = false
    },
    confirmCoupon() {
      this.showCouponModal = false
    },
    formatCouponDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return dateStr
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}/${m}/${d}`
    },
    async handleContinue() {
      if (this.cartItems.length === 0 && this.currentStep === 1) {
        this.showToast({ message: this.$t('cart_modal.empty_cart'), type: 'warning' })
        return
      }
      if (!this.requireLogin('continue')) return

      if (this.currentStep === 1) {
        this.initBillingForm()
        await this.fetchOrderInfo()
        this.currentStep = 2
        return
      }
      if (this.currentStep === 2) {
        if (!this.validateBillingForm()) return
        this.currentStep = 3
        return
      }
      if (this.currentStep === 3) {
        await this.handleConfirmPayment()
        return
      }
      this.handleClose()
    },
    validatePaymentForm() {
      if (!this.ageConfirmed) {
        this.showToast({ message: this.$t('cart_modal.validation.age_required'), type: 'warning' })
        return false
      }
      return true
    },
    cartIdsString() {
      return this.cartItems.map(item => item.cartId || item.id).join(',')
    },
    buildOrderPayload(orderNumber) {
      const isUSDT = this.paymentMethod === 'u-shield'
      const isChinapay = this.paymentMethod === 'chinapay'
      return {
        orderNumber,
        cartids: this.cartIdsString(),
        expresstype: this.deliveryForm.pickupMethod,
        name: this.billingForm.name.trim(),
        phone: this.billingForm.phone.trim(),
        address: this.billingForm.address.trim(),
        remark: this.orderRemark.trim(),
        total: this.orderTotalNum,
        isUSDTpay: isUSDT ? 1 : 0,
        ischinapay: isChinapay ? 1 : 0,
        hkWallet: isUSDT ? 'false' : (this.paymentMethod === 'hk-wallet' ? 'true' : 'false'),
        coupon_id: this.selectedCoupon ? (this.selectedCoupon.id != null ? this.selectedCoupon.id : this.selectedCoupon.id) : ''
      }
    },
    buildOrderInfo(orderNumber, paymentMethod) {
      return {
        id: orderNumber,
        orderNumber,
        date: new Date().toISOString(),
        status: 'unpaid',
        paymentMethod,
        total: this.orderTotalNum,
        subtotal: this.subtotalNum,
        discount: this.discountNum,
        shipping: this.shippingNum,
        billing: { ...this.billingForm },
        shipping: { ...this.shippingForm },
        items: this.cartItems.map(item => ({
          name: item.nameCn || item.name,
          brand: item.brandCn || item.brand,
          unitPrice: this.parsePrice(item.price),
          quantity: item.quantity,
          lineTotal: this.lineTotal(item)
        }))
      }
    },
    async fetchOrderInfo() {
      if (!this.isLoggedIn || this.cartItems.length === 0) return
      this.orderInfoLoading = true
      try {
        const res = await goodsApi.getOrderInfo({ cartids: this.cartIdsString() })
        if (res.code === 1 && res.data) {
          this.freightInfo = res.data.freightinfo || []
          // 若当前选中的送货方式已被后台下架/删除，自动切换为第一个可用方式
          if (this.freightInfo.length && !this.freightInfo.some(f => String(f.type) === String(this.deliveryForm.pickupMethod))) {
            this.deliveryForm.pickupMethod = Number(this.freightInfo[0].type)
          }
          this.usdtPayImg = res.data.usdtpayimg || ''
          this.usdtPayAddr = res.data.usdtpayaddr || ''
          if (res.data.addressinfo) {
            const addr = res.data.addressinfo
            if (addr.name) this.billingForm.name = addr.name
            if (addr.phone) this.billingForm.phone = addr.phone
            if (addr.address) this.billingForm.address = addr.address
          }
        } else {
          setTimeout(() => {
            this.showToast({ message: res.msg || this.$t('cart_modal.fetch_order_failed'), type: 'error',duration: 4000 })
            this.handlePrevious()
          }, 100)
        }
      } catch (e) {
        console.error('获取订单信息失败', e)
        setTimeout(() => {
          this.showToast({ message: res.msg || this.$t('cart_modal.fetch_order_failed'), type: 'error',duration: 4000 })
          this.handlePrevious()
        }, 100)
      } finally {
        this.orderInfoLoading = false
      }
    },
    async fetchCustomerServiceQrCode() {
      if (this.customerServiceQrCode) return
      try {
        const res = await this.$api.home.getContact()
        if (res.code === 1 && res.data && res.data.kefu_qrcode) {
          this.customerServiceQrCode = getFullUrl(res.data.kefu_qrcode)
        }
      } catch (e) {
        console.error('获取客服二维码失败', e)
      }
    },
    getFreightByType(type) {
      const item = this.freightInfo.find(f => String(f.type) === String(type))
      if (!item) return 0
      const price = parseFloat(item.price)
      const freecount = parseInt(item.freecount, 10)
      const totalQty = this.cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
      if (freecount > 0 && totalQty >= freecount) return 0
      return isNaN(price) ? 0 : price
    },
    getFreightDesc(type) {
      const item = this.freightInfo.find(f => String(f.type) === String(type))
      if (!item) return ''
      const price = parseFloat(item.price)
      const freecount = parseInt(item.freecount, 10)
      if (freecount > 0 && !isNaN(price) && price > 0 && this.getFreightByType(type) > 0) {
        return this.$t('cart_modal.free_count_note', { count: freecount })
      }
      return ''
    },
    submitPayForm(payUrl, bianhao) {
      if (!payUrl) return
      const isChinapay = this.paymentMethod === 'chinapay'
      
      // 银联支付：iOS最兼容的方式 - 直接在当前页面提交表单
      if (isChinapay) {
        console.log('ChinaPay支付：准备在当前页面提交表单')
        
        // 查询支付订单状态 - 轮询
        if (bianhao) this.startPayQuery(bianhao)
        
        // 关闭新窗口（如果已打开）
        if (this.chinapayWindow && !this.chinapayWindow.closed) {
          this.chinapayWindow.close()
          this.chinapayWindow = null
        }
        
        // 先关闭所有模态框，清空状态
        this.showAlipayModal = false
        this.isChinapayModal = false
        this.$emit('close')
        
        // 使用$nextTick确保DOM更新完成后再提交表单
        this.$nextTick(() => {
          // 创建隐藏容器来解析表单HTML
          const container = document.createElement('div')
          container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;'
          container.innerHTML = payUrl
          document.body.appendChild(container)
          
          const form = container.querySelector('form')
          if (form) {
            console.log('找到支付表单，表单action:', form.action, '方法:', form.method)
            // 确保表单配置正确
            form.removeAttribute('target')  // 移除target，在当前页面提交
            form.method = form.method || 'POST'
            
            // iOS Safari需要表单在DOM中可见才能正确提交
            form.style.display = 'block'
            
            // 立即提交表单（同步操作，避免异步延迟）
            console.log('提交支付表单...')
            try {
              form.submit()
            } catch (submitError) {
              console.error('表单提交失败:', submitError)
              // 清理容器
              if (container.parentNode) {
                document.body.removeChild(container)
              }
              // 显示错误提示
              this.showToast({ 
                message: this.$t('cart_modal.payment_form_error') || '支付表单提交失败，请重试', 
                type: 'error' 
              })
            }
          } else {
            console.error('未找到支付表单，payUrl内容:', payUrl.substring(0, 200))
            // 清理容器
            if (container.parentNode) {
              document.body.removeChild(container)
            }
            this.showToast({ 
              message: this.$t('cart_modal.payment_form_error') || '支付表单加载失败，请重试', 
              type: 'error' 
            })
          }
        })
        return
      }
      
      // 支付宝/微信支付：iframe 方式
      this.isChinapayModal = false
      this.showAlipayModal = true
      
      // 查询支付订单状态 - 轮询
      if (bianhao) this.startPayQuery(bianhao)
      
      this.$nextTick(() => {
        const container = document.createElement('div')
        container.style.display = 'none'
        container.innerHTML = payUrl
        document.body.appendChild(container)
        const form = container.querySelector('form')
        if (form) {
          form.target = 'alipayFrame'
          form.submit()
        } else {
          const frame = document.querySelector('iframe[name="alipayFrame"]')
          if (frame) frame.src = payUrl
        }
        document.body.removeChild(container)
      })
    },
    fallbackSubmitForm(payUrl, bianhao, target) {
      console.log('使用回退方案提交支付表单到窗口:', target)
      const container = document.createElement('div')
      container.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;'
      container.innerHTML = payUrl
      document.body.appendChild(container)
      const form = container.querySelector('form')
      if (form) {
        // 为 iOS 兼容性，确保表单有正确的属性
        form.target = target || this.chinapayWindowName || '_blank'
        form.method = form.method || 'POST'
        
        console.log('提交表单到:', form.target, '方法:', form.method)
        form.submit()
        
        setTimeout(() => {
          if (container.parentNode) {
            container.parentNode.removeChild(container)
          }
        }, 5000)
      } else {
        console.error('回退方案中未找到表单')
        this.showToast({ 
          message: this.$t('cart_modal.payment_form_error') || '支付表单加载失败，请重试', 
          type: 'error' 
        })
      }
    },
    closeAlipayModal() {
      this.showAlipayModal = false
      this.manualPayChecking = false
    },
    handlePaySuccess() {
      this.stopPayQuery()
      this.showAlipayModal = false
      this.currentStep = 4
      this.$store.dispatch('clearCart')
    },
    async handleManualPaidCheck() {
      if (this.manualPayChecking) return
      if (!this.confirmedOrderNumber) {
        this.showToast({ message: this.$t('cart_modal.payment_query_failed'), type: 'warning' })
        return
      }

      this.manualPayChecking = true
      try {
        const res = await goodsApi.payQuery({ bianhao: this.confirmedOrderNumber })
        if (res.code === 1) {
          this.handlePaySuccess()
          return
        }
        this.showToast({ message: res.msg || this.$t('cart_modal.payment_not_confirmed'), type: 'warning', duration: 4000 })
      } catch (e) {
        this.showToast({ message: this.$t('cart_modal.payment_query_failed'), type: 'error', duration: 4000 })
      } finally {
        this.manualPayChecking = false
      }
    },
    startPayQuery(bianhao) {
      this.stopPayQuery()
      this.payTimer = setInterval(async () => {
        try {
          const res = await goodsApi.payQuery({ bianhao })
          if (res.code === 1) {
            this.handlePaySuccess()
          }
        } catch (e) {
          // 查询失败不停止轮询，等待下次重试
        }
      }, 2000)
    },
    stopPayQuery() {
      if (this.payTimer) {
        clearInterval(this.payTimer)
        this.payTimer = null
      }
    },
    startUSDTQuery(bianhao) {
      this.stopUSDTQuery()
      this.usdtQueryTimer = setInterval(async () => {
        try {
          const res = await goodsApi.usdtQuery({ bianhao })
          if (res.code === 1) {
            this.stopUSDTQuery()
            this.showUSDTConfirmModal = false
            this.currentStep = 4
            this.$store.dispatch('clearCart')
          }
        } catch (e) {
          // 查询失败不停止轮询，等待下次重试
        }
      }, 2000)
    },
    stopUSDTQuery() {
      if (this.usdtQueryTimer) {
        clearInterval(this.usdtQueryTimer)
        this.usdtQueryTimer = null
      }
    },
    async handleConfirmPayment() {
      if (this.paying) return
      if (!this.validatePaymentForm()) return
      
      this.paying = true
      try {
        const orderNumber = this.generateOrderNumber()
        this.confirmedOrderTotal = this.orderTotalNum
        const payload = this.buildOrderPayload(orderNumber)
        const result = await this.placeOrder(payload)
        if (!result) {
          return
        }
        const [payUrl, bianhao] = result
        this.confirmedOrderNumber = bianhao
        this.$store.commit('ADD_ORDER', this.buildOrderInfo(orderNumber, this.paymentMethod))
        if (this.paymentMethod === 'u-shield') {
          this.showUSDTConfirmModal = true
          this.startUSDTQuery(bianhao)
          return
        }
        if (payUrl) {
          this.submitPayForm(payUrl, bianhao)
        } else {
          // 没有支付URL时才进入完成步骤
          this.currentStep = 4
          this.$store.dispatch('clearCart')
        }
      } catch (e) {
        console.error('下單失敗', e)
        this.showToast({ message: (e && e.message) || this.$t('cart_modal.fetch_order_failed'), type: 'error', duration: 4000 })
      } finally {
        this.paying = false
      }
    },
    confirmUShieldTransfer() {
      this.showUShieldModal = false
      this.currentStep = 4
      this.$store.dispatch('clearCart')
    },
    copyUsdtAddr() {
      if (!this.usdtPayAddr) return
      const text = this.usdtPayAddr
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.showToast({ message: this.$t('cart_modal.u_shield_modal.copy_success'), type: 'success' })
        }).catch(() => {
          this.fallbackCopy(text)
        })
      } else {
        this.fallbackCopy(text)
      }
    },
    fallbackCopy(text) {
      const input = document.createElement('textarea')
      input.value = text
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        this.showToast({ message: this.$t('cart_modal.u_shield_modal.copy_success'), type: 'success' })
      } catch (e) {
        this.showToast({ message: this.$t('cart_modal.u_shield_modal.copy_failed'), type: 'error' })
      }
      document.body.removeChild(input)
    },
    isOtherModalOpen() {
      const s = this.$store.state
      return s.loginModalVisible || s.registerModalVisible || s.forgotPasswordModalVisible
    }
  },
  beforeDestroy() {
    this.stopPayQuery()
    this.stopUSDTQuery()
    // 清理可能打开的银联支付窗口
    if (this.chinapayWindow && !this.chinapayWindow.closed) {
      this.chinapayWindow.close()
      this.chinapayWindow = null
    }
  }
}
</script>

<style lang="scss" scoped>
.cart-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
  }
}

.cart-modal-container {
  background: #fff;
  width: 1080px;
  max-width: 92vw;
  max-width: min(92vw, 1080px);
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    height: 92vh;
    height: -webkit-fill-available;
    border-radius: 12px 12px 0 0;
    box-sizing: border-box;
  }
}

/* 顶部标题栏 */
.cart-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 28px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
}

.cart-modal-title {
  font-size: 17px;
  color: #666;
  font-weight: 400;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 6px;
}

.header-btn {
  padding: 8px 20px;
  font-size: 13px;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s;
  letter-spacing: 0.5px;

  &:hover {
    opacity: 0.85;
  }
}

.btn-grey {
  background: #888;
  color: #fff;
}

.btn-dark {
  background: #2a2a2a;
  color: #fff;
}

/* 进度步骤 */
.checkout-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 28px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

.step {
  font-size: 13px;
  color: #ccc;
  padding: 5px 12px;
  position: relative;
  white-space: nowrap;
  min-width: 0;

  &.active {
    color: #333;
    font-weight: 500;
    background: #f0f0f0;

    &::after {
      content: '';
      position: absolute;
      right: -10px;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-top: 14px solid transparent;
      border-bottom: 14px solid transparent;
      border-left: 10px solid #8b7e55;
      z-index: 1;
    }
  }

  &.completed {
    color: #666;
  }
}

.step-chevron {
  font-size: 12px;
  color: #ddd;
  margin: 0 6px;
}

/* 汇总栏 */
.summary-bar {
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

.summary-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 22px;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;

  .summary-label {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
    white-space: nowrap;
  }

  .summary-value {
    font-size: 14px;
    color: #fff;
    font-weight: 500;
    white-space: nowrap;
    overflow-wrap: break-word;
  }

  &.segment-total .total-value {
    font-size: 18px;
    font-weight: 700;
  }
}

.segment-dark {
  background: #1a1a1a;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.9;
  }
}

.segment-gold {
  background: #8b7e55;
  flex: 1.2;
}

.segment-grey {
  background: #4a4a4a;
}

.segment-total {
  flex: 1.3;
}

/* 積分欄 */
.points-bar {
  display: flex;
  align-items: center;
  padding: 12px 28px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  gap: 0;
  background: #fff;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
}

.points-logo {
  flex-shrink: 0;
  margin-right: 20px;

  img {
    height: 22px;
    width: auto;
    object-fit: contain;
  }
}

.points-item {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  border-right: 1px solid #eee;

  &:last-child {
    border-right: none;
  }
}

.points-label {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}

.points-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;

  &.points-total {
    color: #8b7e55;
    font-weight: 700;
  }
}

/* 商品列表 */
.cart-items-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
}

.cart-empty {
  text-align: center;
  padding: 60px 20px;

  p {
    font-size: 15px;
    color: #999;
    margin-bottom: 20px;
  }
}

.continue-shopping-btn {
  padding: 10px 28px;
  background: #2a2a2a;
  color: #fff;
  border: none;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #8b7e55;
  }
}

.cart-item-row {
  display: flex;
  align-items: center;
  padding: 22px 28px;
  min-height: 100px;
  border-bottom: 1px solid #eee;
  gap: 22px;
  position: relative;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  &:last-child {
    border-bottom: none;
  }
}

.item-thumb {
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  border: 1px solid #eee;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.thumb-placeholder {
  font-size: 12px;
  color: #999;
  text-align: center;
  padding: 4px;
}

.item-details {
  flex: 1;
  min-width: 0;
  width: 0;
}

.item-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.item-brand-tag {
  display: inline-block;
  font-size: 11px;
  color: #4a6a8a;
  background: #dce8f2;
  padding: 2px 8px;
}

.item-promo-tag {
  display: inline-block;
  font-size: 11px;
  color: #c45a20;
  background: #fde8d8;
  padding: 2px 8px;
}

.item-name {
  font-size: 17px;
  color: #222;
  font-weight: 700;
  margin: 0 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.item-spec {
  font-size: 13px;
  color: #999;
  margin: 0 0 4px;
}

.item-sku {
  font-size: 12px;
  color: #bbb;
  margin: 0;
}

.item-unit-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 80px;
  flex-shrink: 0;
  max-width: 90px;
}

.current-price {
  font-size: 14px;
  color: #c02c38;
  font-weight: 500;
}

.original-price {
  font-size: 12px;
  color: #bbb;
  text-decoration: line-through;
  margin-top: 3px;
}

.item-quantity {
  display: flex;
  border: 1px solid #ddd;
  flex-shrink: 0;

  .qty-btn {
    width: 28px;
    height: 28px;
    background: #fff;
    border: none;
    cursor: pointer;
    font-size: 15px;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s;

    &:hover {
      background: #f5f5f5;
    }
  }

  input {
    width: 34px;
    height: 28px;
    border: none;
    border-left: 1px solid #ddd;
    border-right: 1px solid #ddd;
    text-align: center;
    font-size: 13px;
    color: #333;
    outline: none;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

.item-line-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 80px;
  text-align: right;
  flex-shrink: 0;
  gap: 4px;
}

.discount-badge {
  font-size: 11px;
  color: #8b7e55;
  border: 1px solid #c9b98a;
  padding: 1px 6px;
  white-space: nowrap;
}

.line-total-value {
  font-size: 18px;
  color: #8B7E55;
  font-weight: 700;
  white-space: nowrap;
}

.item-remove {
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  color: #bbb;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: color 0.3s;
  padding: 0;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: #c02c38;
  }
}

/* 第二步：左右布局 */
.checkout-form-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: 24px 28px;
  background: #f7f7f7;
  width: 100%;
  box-sizing: border-box;
}

.checkout-layout {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 20px;
  align-items: start;
  min-width: 0;
}

.checkout-left,
.checkout-right {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.checkout-page-title {
  font-size: 18px;
  font-weight: 700;
  color: #8B7E55;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  .title-icon {
    width: 22px;
    height: 22px;
    color: #8B7E55;
  }
}

.checkout-block {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  width: 100%;
  box-sizing: border-box;
}

.checkout-block-title {
  font-size: 15px;
  font-weight: 700;
  color: #8B7E55;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  .title-icon {
    width: 18px;
    height: 18px;
    color: #8B7E55;
  }
}

.checkout-block-tip {
  font-size: 12px;
  color: #888;
  margin: -8px 0 14px;
}

.delivery-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.delivery-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: #8B7E55;
  }

  &.active {
    border-color: #8B7E55;
    background: #f8f6f0;
  }
}

.delivery-option-input {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  accent-color: #8B7E55;
}

.delivery-option-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.delivery-option-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.delivery-option-desc {
  font-size: 12px;
  color: #888;
}

.delivery-option-fee {
  font-size: 14px;
  color: #8B7E55;
  font-weight: 600;
}

.receiver-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.checkout-label {
  display: block;
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}

.checkout-input,
.checkout-select,
.checkout-textarea {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: #333;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: #8B7E55;
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
  }
}

.checkout-textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

.checkout-summary-block {
  position: sticky;
  top: 0;
}

.checkout-product-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
}

.checkout-product-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  .product-thumb {
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f8f8;
    border: 1px solid #eee;
    border-radius: 4px;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    .thumb-placeholder {
      font-size: 10px;
      color: #999;
      text-align: center;
      padding: 2px;
    }
  }

  .product-info {
    flex: 1;
    min-width: 0;
    width: 0;
  }

  .product-name {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    margin: 0 0 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .product-spec {
    font-size: 12px;
    color: #888;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .product-price {
    font-size: 14px;
    color: #8B7E55;
    font-weight: 600;
    flex-shrink: 0;
  }
}

.remark-field {
  margin-bottom: 16px;
}

.checkout-amounts {
  border-top: 1px solid #eee;
  padding-top: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #666;

  &.amount-total {
    font-size: 15px;
    color: #333;
    font-weight: 700;
    margin-top: 4px;
    padding-top: 10px;
    border-top: 1px solid #eee;
  }
}

.submit-order-btn {
  width: 100%;
  padding: 14px;
  background: #8B7E55;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.3s;
  margin-bottom: 10px;

  &:hover {
    background: #766a45;
  }

  .btn-icon {
    width: 18px;
    height: 18px;
  }
}

.checkout-security-note {
  font-size: 12px;
  color: #666;
  text-align: center;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  .security-icon {
    width: 12px;
    height: 12px;
    color: #666;
  }
}

/* 第三步：訂單確認及付款 */
.checkout-payment-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: 28px 32px 20px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.checkout-payment-left {
  flex: 1;
  min-width: 0;

  .payment-section,
  .payment-agreement,
  .age-confirm {
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
  }
}

.checkout-payment-right {
  width: 360px;
  flex-shrink: 0;
}

.payment-section-title {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin: 0 0 12px;
}

.payment-options {
  border: 1px solid #ddd;
  overflow: hidden;
}

.payment-option {
  display: block;
  width: 100%;
  padding: 14px 20px;
  font-size: 14px;
  color: #333;
  background: #fff;
  border: none;
  border-bottom: 1px solid #ddd;
  cursor: pointer;
  text-align: center;
  transition: background 0.2s, color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover:not(.active) {
    background: #fafafa;
  }

  &.active {
    background: #8b7e55;
    color: #fff;
    font-weight: 500;
  }
}

.payment-agreement {
  margin-top: 24px;
}

.payment-agreement-title {
  font-size: 13px;
  color: #666;
  margin: 0 0 8px;
}

.payment-agreement-note {
  margin: 8px 0 0;
  font-size: 12px;
  color: #b8934d;
  line-height: 1.6;
}

.payment-agreement-list {
  margin: 0;
  padding-left: 18px;
  font-size: 12px;
  color: #888;
  line-height: 1.6;
}

.age-confirm {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 20px;
  cursor: pointer;
}

.age-confirm-input {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: #8b7e55;
  cursor: pointer;
}

.age-confirm-text {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
}

.checkout-payment-footer {
  flex-shrink: 0;
  padding: 16px 32px 24px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
}

.confirm-payment-btn {
  min-width: 280px;
  padding: 14px 48px;
  background: #2a2a2a;
  color: #fff;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #8b7e55;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  &.is-loading {
    background: #555;
  }
}

.payment-btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: payment-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes payment-spin {
  to { transform: rotate(360deg); }
}

/* 第四步：完成訂單 */
.checkout-complete-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  text-align: center;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

.payment-success-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #4caf50;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.payment-process-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 24px;
}

.order-info-card,
.payment-instruction-card,
.customer-service-card {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 24px 32px;
  box-sizing: border-box;
}

.order-info-card {
  margin-bottom: 16px;
}

.order-info-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px 0;

  & + & {
    margin-top: 4px;
  }
}

.order-info-label {
  font-size: 14px;
  color: #666;
}

.order-info-value {
  font-size: 15px;
  color: #333;
  font-weight: 500;
}

.payment-instruction-card {
  line-height: 1.7;
}

.payment-instruction-main {
  font-size: 14px;
  color: #555;
  margin: 0 0 8px;
}

.payment-instruction-sub {
  font-size: 13px;
  color: #999;
  margin: 0;
}

.customer-service-card {
  margin-top: 16px;
  text-align: center;
}

.customer-service-title {
  margin: 0 0 12px;
  color: #333;
  font-size: 15px;
  font-weight: 600;
}

.customer-service-qrcode {
  width: 150px;
  height: 150px;
  object-fit: contain;
  display: block;
  margin: 0 auto 10px;
}

.customer-service-desc {
  margin: 0;
  color: #888;
  font-size: 13px;
  line-height: 1.5;
}

/* Transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .cart-modal-container,
.modal-fade-leave-active .cart-modal-container {
  transition: transform 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .cart-modal-container,
.modal-fade-leave-to .cart-modal-container {
  transform: translateY(12px);
}

/* 响应式 */
@media (max-width: 900px) {
  .checkout-layout {
    grid-template-columns: 1fr;
  }

  .checkout-summary-block {
    position: static;
  }

  .checkout-form-body,
  .checkout-payment-body {
    padding: 20px;
  }

  .checkout-payment-body {
    flex-direction: column;
  }

  .checkout-payment-right {
    width: 100%;
  }

  .payment-section,
  .payment-agreement,
  .age-confirm {
    max-width: 100%;
  }

  .checkout-payment-footer {
    padding: 14px 20px 20px;
  }

  .confirm-payment-btn {
    width: 100%;
    min-width: 0;
  }

  .cart-modal-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 20px;
  }

  .summary-bar {
    flex-wrap: wrap;
  }

  .summary-segment {
    padding: 12px 16px;
    min-width: 50%;

    &.segment-total {
      width: 100%;
      flex: none;
    }
  }

  .cart-item-row {
    flex-wrap: wrap;
    padding: 18px 20px;
    min-height: auto;
    gap: 14px;
  }

  .item-details {
    width: calc(100% - 96px);
  }

  .item-name {
    font-size: 15px;
  }

  .line-total-value {
    font-size: 18px;
  }

  .item-line-total {
    min-width: auto;
  }

  .points-bar {
    flex-wrap: wrap;
    padding: 12px 20px;
    gap: 10px;
  }

  .points-logo {
    width: 100%;
    margin-right: 0;
    margin-bottom: 4px;
  }

  .points-item {
    flex: 1 1 40%;
    border-right: none;
    justify-content: flex-start;
    padding: 4px 0;
  }

  .item-unit-price,
  .item-quantity {
    min-width: auto;
  }

  .item-remove {
    position: absolute;
    top: 16px;
    right: 16px;
  }
}

@media (max-width: 768px) {
  .cart-modal-header {
    padding: 12px 16px;
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }

  .cart-modal-title {
    font-size: 15px;
    flex: 1;
  }

  .header-actions {
    width: auto;
    gap: 4px;
  }

  .header-btn {
    padding: 5px 8px;
    font-size: 11px;
    white-space: nowrap;
  }

  .continue-btn {
    min-width: 48px;
    padding: 5px 10px;
  }

  .checkout-steps {
    padding: 10px 8px;
    font-size: 12px;
    gap: 1px;
  }

  .step {
    padding: 3px 6px;
    font-size: 11px;
  }

  .step-chevron {
    margin: 0 1px;
  }

  .summary-bar {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: 100%;
    box-sizing: border-box;
  }

  .summary-segment {
    min-width: 0;
    padding: 8px 4px;
    flex-direction: column;
    gap: 2px;
    box-sizing: border-box;

    .summary-label {
      font-size: 9px;
    }

    .summary-value {
      font-size: 11px;
      white-space: normal;
    }

    &.segment-total {
      grid-column: span 3;
      flex-direction: row;
      justify-content: space-between;
      width: auto;
      padding: 8px 12px;

      .summary-value {
        font-size: 14px;
        white-space: nowrap;
      }
    }
  }

  .points-bar {
    padding: 10px 12px;
    flex-wrap: wrap;
  }

  .points-logo {
    width: 100%;
    margin-right: 0;
    margin-bottom: 6px;
  }

  .points-logo img {
    height: 16px;
  }

  .points-item {
    flex: 1 1 45%;
    padding: 2px 0;
    border-right: none;
    min-width: 0;
  }

  .points-label {
    font-size: 10px;
  }

  .points-value {
    font-size: 12px;
  }

  .cart-items-body {
    padding-bottom: 20px;
  }

  .cart-item-row {
    padding: 14px 12px;
    gap: 8px;
    position: relative;
    flex-wrap: wrap;
  }

  .item-thumb {
    width: 56px;
    height: 56px;
  }

  .item-details {
    width: calc(100% - 68px);
    min-width: 0;
  }

  .item-name {
    font-size: 14px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-spec,
  .item-sku {
    font-size: 11px;
  }

  .item-unit-price {
    align-items: flex-start;
    min-width: auto;
    flex: 1;
  }

  .item-quantity {
    margin-left: auto;
  }

  .item-line-total {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 4px;
    min-width: 0;
  }

  .line-total-value {
    font-size: 16px;
  }

  .item-remove {
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .checkout-form-body {
    padding: 12px;
  }

  .checkout-page-title {
    font-size: 15px;
  }

  .checkout-block {
    padding: 14px;
  }

  .delivery-option {
    padding: 10px;
    align-items: flex-start;
  }

  .delivery-option-info {
    gap: 1px;
    min-width: 0;
    flex: 1;
  }

  .delivery-option-name {
    font-size: 12px;
  }

  .delivery-option-desc {
    font-size: 10px;
  }

  .delivery-option-fee {
    font-size: 12px;
    white-space: nowrap;
  }

  .checkout-input,
  .checkout-textarea {
    padding: 9px 11px;
    font-size: 13px;
  }

  .checkout-product-item {
    gap: 10px;
    min-width: 0;
  }

  .product-thumb {
    width: 44px;
    height: 44px;
  }

  .product-name {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .product-spec {
    font-size: 11px;
  }

  .checkout-amounts {
    gap: 8px;
  }

  .amount-row {
    font-size: 12px;
  }

  .submit-order-btn {
    padding: 12px;
    font-size: 14px;
  }

  .checkout-payment-body {
    padding: 16px;
  }

  .payment-option {
    padding: 12px 16px;
    font-size: 13px;
  }

  .checkout-complete-body {
    padding: 24px 16px;
  }

  .order-info-card,
  .payment-instruction-card,
  .customer-service-card {
    padding: 18px 16px;
  }

  .address-modal-container {
    width: 100%;
    max-width: 100%;
    height: 70vh;
    max-height: 70vh;
    border-radius: 12px 12px 0 0;
    position: fixed;
    bottom: 0;
    left: 0;
  }

  .address-modal-overlay {
    align-items: flex-end;
    padding: 0;
  }
}

@media (max-width: 600px) {
  .checkout-steps {
    padding: 10px 8px;
  }

  .step {
    font-size: 11px;
    padding: 3px 6px;
  }

  .step-chevron {
    margin: 0 1px;
  }
}

/* 地址簿彈窗 */
.address-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.address-modal-container {
  background: #fff;
  width: 600px;
  max-width: 90vw;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.address-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}

.address-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.address-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
}

.address-modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.address-empty {
  text-align: center;
  padding: 40px 0;
  color: #999;
}

.address-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9f9f9;
  }
}

.address-item-info {
  flex: 1;
}

.address-item-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px;
}

.address-item-phone {
  font-size: 13px;
  color: #666;
  margin: 0 0 4px;
}

.address-item-detail {
  font-size: 13px;
  color: #888;
  margin: 0;
}

.address-item-btn {
  padding: 6px 20px;
  font-size: 13px;
  background: #2a2a2a;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.85;
  }
}

/* 優惠券彈窗 */
.coupon-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.coupon-modal-container {
  background: #fff;
  width: 640px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.coupon-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}

.coupon-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.coupon-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
}

.coupon-modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.coupon-empty {
  text-align: center;
  padding: 40px 0;
  color: #999;
}

.coupon-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.coupon-card {
  display: flex;
  border: 1px solid #e5e5e5;
  cursor: pointer;
  transition: all 0.25s;
  overflow: hidden;

  &:hover {
    border-color: #c0a062;
  }

  &.selected {
    border-color: #c0a062;
    background: #fbf7f0;
  }
}

.coupon-card-left {
  width: 140px;
  background: #2a2a2a;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  flex-shrink: 0;
}

.coupon-card.selected .coupon-card-left {
  background: #c0a062;
}

.coupon-value-large {
  display: flex;
  align-items: baseline;
  margin-bottom: 6px;
}

.coupon-currency-small {
  font-size: 14px;
  margin-right: 2px;
}

.coupon-amount-large {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.coupon-condition {
  font-size: 11px;
  opacity: 0.8;
  text-align: center;
}

.coupon-card-right {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
}

.coupon-card-details {
  flex: 1;
}

.coupon-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin: 0 0 6px;
}

.coupon-card-desc {
  font-size: 13px;
  color: #888;
  margin: 0 0 8px;
  line-height: 1.4;
}

.coupon-card-validity {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
}

.coupon-card-check {
  color: #c0a062;
  display: flex;
  align-items: center;
  margin-left: 12px;
}

.coupon-card {
  position: relative;

  &.coupon-disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      border-color: #e5e5e5;
    }
  }
}

.coupon-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.coupon-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;

  &.type-product {
    background: #e3f2fd;
    color: #1976d2;
  }

  &.type-brand {
    background: #f3e5f5;
    color: #7b1fa2;
  }

  &.type-shipping {
    background: #e0f7fa;
    color: #00838f;
  }
}

.coupon-value-free-shipping {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  svg {
    color: #fff;
  }
}

.free-shipping-text {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}

.coupon-not-applicable {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 0, 0, 0.08);
  padding: 4px 12px;
  font-size: 11px;
  color: #d32f2f;
  text-align: center;
}

.coupon-modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #eee;
  justify-content: flex-end;
}

.coupon-modal-btn {
  padding: 8px 28px;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.85;
  }
}

.btn-clear {
  background: #f0f0f0;
  color: #666;
}

.btn-confirm {
  background: #2a2a2a;
  color: #fff;
}

/* U盾轉賬提示彈窗 */
.ushield-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
}

.ushield-modal-container {
  background: #fff;
  width: 420px;
  max-width: 90vw;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.ushield-modal-title {
  font-size: 16px;
  color: #c02c38;
  font-weight: 600;
  margin: 0 0 20px;
  line-height: 1.5;
}

.ushield-qr-img {
  width: 100%;
  height: auto;
  max-width: 100%;
  margin-bottom: 20px;
  border: 1px solid #eee;
}

.ushield-addr-section {
  width: 100%;
  margin-bottom: 24px;
}

.ushield-addr-label {
  font-size: 14px;
  color: #333;
  margin: 0 0 8px;
  font-weight: 500;
}

.ushield-addr-text {
  font-size: 15px;
  color: #c02c38;
  font-weight: 600;
  word-break: break-all;
  cursor: pointer;
  margin: 0 0 6px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
}

.ushield-addr-hint {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.ushield-modal-btn {
  padding: 12px 48px;
  font-size: 15px;
  background: #2a2a2a;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.85;
  }
}

/* USDT 支付右侧面板 */
.usdt-payment-panel {
  background: #fff;
  border: 1px solid #eee;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.usdt-payment-title {
  font-size: 16px;
  color: #c02c38;
  font-weight: 600;
  margin: 0 0 20px;
  line-height: 1.5;
}

.usdt-payment-qr {
  width: 100%;
  height: auto;
  max-width: 100%;
  margin-bottom: 20px;
  border: 1px solid #eee;
}

.usdt-payment-addr-section {
  width: 100%;
}

.usdt-payment-addr-label {
  font-size: 14px;
  color: #333;
  margin: 0 0 8px;
  font-weight: 500;
}

.usdt-payment-addr-text {
  font-size: 14px;
  color: #c02c38;
  font-weight: 600;
  word-break: break-all;
  cursor: pointer;
  margin: 0 0 6px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
}

.usdt-payment-addr-hint {
  font-size: 12px;
  color: #999;
  margin: 0 0 16px;
}

.usdt-payment-desc {
  font-size: 13px;
  color: #666;
  text-align: left;
  margin: 0 0 8px;
  line-height: 1.5;
}

/* USDT 等待客服确认弹窗 */
.usdt-confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2200;
}

.usdt-confirm-container {
  background: #fff;
  width: 360px;
  max-width: 90vw;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.usdt-confirm-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid #eee;
  border-top-color: #8b7e55;
  border-radius: 50%;
  animation: usdt-spin 1s linear infinite;
  margin-bottom: 24px;
}

@keyframes usdt-spin {
  to {
    transform: rotate(360deg);
  }
}

.usdt-confirm-title {
  font-size: 16px;
  color: #333;
  font-weight: 600;
  margin: 0 0 12px;
  line-height: 1.5;
}

.usdt-confirm-order {
  font-size: 13px;
  color: #666;
  margin: 0;
  word-break: break-all;
}

/* 支付寶支付彈窗 */
.alipay-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
}

.alipay-modal-container {
  background: #fff;
  width: 480px;
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.chinapay-modal {
  width: 900px;
  height: 85vh;
}

.alipay-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}

.alipay-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.alipay-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
}

.alipay-modal-body {
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.alipay-iframe {
  width: 100%;
  height: 100%;
  min-height: 60vh;
  border: 0;
}

.chinapay-modal .alipay-iframe {
  width: 100%;
  height: 100%;
  min-height: 60vh;
}

.chinapay-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.chinapay-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #eee;
  border-top-color: #333;
  border-radius: 50%;
  animation: chinapay-spin 0.8s linear infinite;
}

@keyframes chinapay-spin {
  to { transform: rotate(360deg); }
}

.chinapay-waiting-text {
  font-size: 14px;
  color: #666;
  margin: 0;
  text-align: center;
}

.alipay-modal-footer {
  padding: 14px 24px 18px;
  border-top: 1px solid #eee;
  text-align: center;
}

.alipay-paid-btn {
  min-width: 160px;
  padding: 10px 28px;
  border: none;
  background: #1a1a1a;
  color: #d4af37;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.alipay-paid-hint {
  margin: 8px 0 0;
  color: #999;
  font-size: 12px;
  line-height: 1.5;
}

.display-none {
  display: none;
}
@media screen and (max-width: 768px) {
  .checkout-payment-right {
    display: none;
  }
  .display-none {
    display: block;
  }
}
</style>
